/**
 * useCaptureIntegrity — forked from vCIP_droom_4.0/src/media/useCaptureIntegrity.ts (S5-4; the
 * layer-2 injection / virtual-camera fingerprint collector) and re-targeted at
 * API_CONTRACT_SV_v0.1 §2 `POST /telemetry {fingerprint, permissions, clientTime}`.
 *
 * Honesty rules carried over verbatim:
 *  • Collect ONCE per distinct live video track; a re-acquired stream (lens switch, next shot)
 *    collects again — a legitimate new attempt.
 *  • A collection/post failure NEVER blocks capture; a missing signal is the reviewer's concern.
 *  • The fingerprint is CLIENT-REPORTED and attacker-influenced by construction. It is a
 *    probabilistic consistency signal (`Advisory signal` server-side), never proof of provenance.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { postTelemetry } from '@/api/capture';
import { useCaptureStore } from '@/store/captureStore';
import { collectCaptureFingerprint } from './captureFingerprint';

/** At most this many fingerprint posts per page load — one per lens/shot would be noise, not signal. */
export const MAX_COLLECTIONS = 3;
let collections = 0;

/**
 * US-03482 — the fingerprint must reach a SEALED shot (Injection RCA, 2026-11-03). The be files each telemetry post on
 * the last shot that has received a chunk, and silently drops a post made before any shot has one — which is exactly
 * when the first collection happens (step 1). With ONE camera session per capture (G1) there is one video track, so no
 * later collection would come either, and SV_INJECTION would always read "no capture fingerprint". So the last
 * collected fingerprint is CACHED (the same collection, never a new one):
 *   • re-posted after the first shot the server has acknowledged (`repostFingerprintAfterFirstShot`) — at most
 *     MAX_REPOSTS times, a failed post refunded so the next acknowledged shot tries again; unnecessary when the
 *     collection itself was posted after a shot was already done;
 *   • carried by the submit-time telemetry (`fingerprintForSubmit`) — NOT counted against the cap, so a retried submit
 *     never sends less; with nothing collected the field is OMITTED, never `null` (null overwrote the last shot's).
 * The collected track id lives here too (module level): every shot screen mounts its own integrity hook, but with one
 * camera session it is the same track — it must not be collected again per step.
 *
 * US-03482 round 3 (R2-7, §16k): the fingerprint carries the nonce the server issued for this capture (`POST /capture/start`,
 * held in the store as `visit.nonce`) — never one minted here. The be judges only a fingerprint that echoes it; one made
 * for another capture is stored but set aside. With no issued nonce yet nothing is collected (the start answer re-runs it).
 */
export const MAX_REPOSTS = 2;
let cached: unknown = null;
let reposts = 0;
let landedOnDoneShot = false;
let collectedTrackId: string | null = null;

export function resetIntegrityForTests(): void {
  collections = 0;
  cached = null;
  reposts = 0;
  landedOnDoneShot = false;
  collectedTrackId = null;
}

/** The last collected fingerprint (this page load), or null. */
export function cachedCaptureFingerprint(): unknown {
  return cached;
}

/** After an acknowledged shot: re-post the cached fingerprint (once it lands, never again), within MAX_REPOSTS. */
export async function repostFingerprintAfterFirstShot(permissions: Readonly<Record<string, string | boolean | null>>): Promise<boolean> {
  if (cached === null || landedOnDoneShot || reposts >= MAX_REPOSTS) return false;
  landedOnDoneShot = true;
  reposts += 1;
  try {
    await postTelemetry({ fingerprint: cached, permissions, clientTime: new Date().toISOString() });
    return true;
  } catch {
    landedOnDoneShot = false; // the next acknowledged shot tries again …
    reposts -= 1; // … and a failed post does not use up the cap
    return false;
  }
}

/** What the submit-time telemetry carries: the cached fingerprint, or `undefined` (omit the field). Never capped. */
export function fingerprintForSubmit(): unknown {
  return cached === null ? undefined : cached;
}

export type CaptureIntegrityPhase = 'idle' | 'collecting' | 'posted' | 'skipped' | 'failed';

export interface CaptureIntegrityState {
  readonly phase: CaptureIntegrityPhase;
  readonly recollect: () => void;
}

export function useCaptureIntegrity(
  getStream: () => MediaStream | null,
  enabled: boolean,
  permissions: () => Readonly<Record<string, string | boolean | null>>,
): CaptureIntegrityState {
  const [phase, setPhase] = useState<CaptureIntegrityPhase>('idle');
  const disposed = useRef(false);
  const permsRef = useRef(permissions);
  permsRef.current = permissions;
  const issuedNonce = useCaptureStore((s) => s.visit?.nonce ?? null);

  useEffect(() => {
    disposed.current = false;
    return () => {
      disposed.current = true;
    };
  }, []);

  const collect = useCallback(async (): Promise<void> => {
    const stream = getStream();
    const track = stream?.getVideoTracks?.()[0];
    if (!stream || !track || !issuedNonce) {
      setPhase('idle');
      return;
    }
    if (collectedTrackId === track.id) return;
    if (collections >= MAX_COLLECTIONS) {
      setPhase('skipped');
      return;
    }
    collectedTrackId = track.id;
    collections += 1;
    setPhase('collecting');
    try {
      const fingerprint = await collectCaptureFingerprint(stream, { nonce: issuedNonce });
      if (disposed.current) return;
      if (!fingerprint) {
        setPhase('skipped');
        return;
      }
      cached = fingerprint;
      // Posted while a shot is already acknowledged: the be files it on that shot — no re-post needed after it.
      const shotDone = useCaptureStore.getState().completed.length > 0;
      await postTelemetry({ fingerprint, permissions: permsRef.current(), clientTime: new Date().toISOString() });
      if (shotDone) landedOnDoneShot = true;
      if (disposed.current) return;
      setPhase('posted');
    } catch {
      if (disposed.current) return;
      setPhase('failed');
    }
  }, [getStream, issuedNonce]);

  useEffect(() => {
    if (!enabled) return;
    void collect();
  }, [enabled, collect]);

  const recollect = useCallback(() => {
    collectedTrackId = null;
    void collect();
  }, [collect]);

  return { phase, recollect };
}
